import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { FirebaseService } from "@shared/lib/services/firebase.service";
import { ToastService } from "@shared/lib/services/toast.service";
import { UploadType } from "@shared/lib/types/briefInfluencerV2";
import { Button, Input, Popover } from "@shared/ui";
import { Close } from "@shared/ui/icons";
import {
  Check,
  FileText,
  Link as LinkIcon,
  Plus,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

export interface UploadItem {
  url: string;
  type: UploadType;
}

interface UploadCellProps {
  fieldKey: string;
  briefInfluencerId: string;
  value: UploadItem[];
  onChange: (items: UploadItem[]) => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
}

export function UploadCell({
  fieldKey,
  briefInfluencerId,
  value,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: UploadCellProps) {
  const { briefId } = useBriefStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !fileInputRef.current?.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
        setIsLinkInputOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddLink = () => {
    if (linkUrl.trim()) {
      let finalUrl = linkUrl.trim();
      if (!finalUrl.match(/^https?:\/\//i)) {
        finalUrl = "https://" + finalUrl;
      }
      const newItem: UploadItem = {
        type: UploadType.LINK,
        url: finalUrl,
      };
      onChange([...value, newItem]);
      setLinkUrl("");
      setIsLinkInputOpen(false);
      setIsMenuOpen(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setUploading(true);
      setIsMenuOpen(false); // Close menu once upload starts

      try {
        const uploadPromises = files.map((file) =>
          FirebaseService.uploadFileToFirebase(
            `campaign-briefs/${briefId}/custom-fields/${fieldKey}`,
            file
          )
        );

        const urls = await Promise.all(uploadPromises);

        const newItems: UploadItem[] = urls.map((url) => ({
          type: UploadType.FILE,
          url,
        }));

        onChange([...value, ...newItems]);
        ToastService.success(`${files.length} file(s) uploaded successfully`);
      } catch (error) {
        ToastService.error("One or more uploads failed. Please try again.");
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const removeItem = (index: number) => {
    const newItems = value.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const renderUploadMenu = () => (
    <div className={styles.uploadMenu}>
      {!isLinkInputOpen ? (
        <>
          <button
            onClick={() => fileInputRef.current?.click()}
            className={styles.uploadMenuItem}
            disabled={uploading}
          >
            <UploadCloud size={12} />
            Upload File
          </button>
          <button
            onClick={() => setIsLinkInputOpen(true)}
            className={styles.uploadMenuItem}
          >
            <LinkIcon size={12} />
            Add Link
          </button>
        </>
      ) : (
        <div className={styles.linkInputContainer}>
          <Input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://"
            size="small"
            autoFocus
            onPressEnter={handleAddLink}
            className={styles.linkInput}
          />
          <div className={styles.linkInputActions}>
            <Button
              size="small"
              onClick={() => {
                setIsLinkInputOpen(false);
                setLinkUrl("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={handleAddLink}
              disabled={!linkUrl.trim()}
            >
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderSingleItem = (item: UploadItem, idx: number) => (
    <div key={idx} className={styles.uploadItem}>
      {item.type === UploadType.LINK ? (
        <LinkIcon size={10} className={styles.uploadItemLinkIcon} />
      ) : (
        <FileText size={10} className={styles.uploadItemIcon} />
      )}
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer"
        className={styles.uploadItemLink}
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === UploadType.LINK
          ? item.url.replace(/^https?:\/\//i, "").slice(0, 10)
          : item.url.split("/").pop()?.slice(0, 10) || "File"}
      </a>
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeItem(idx);
        }}
        className={styles.uploadItemRemove}
      >
        <X size={10} />
      </button>
    </div>
  );

  const renderAllItems = () => (
    <div className={styles.uploadItemsPopover}>
      {value.map((item, idx) => (
        <div key={idx} className={styles.uploadItemPopover}>
          {item.type === UploadType.LINK ? (
            <LinkIcon size={10} className={styles.uploadItemLinkIcon} />
          ) : (
            <FileText size={10} className={styles.uploadItemIcon} />
          )}
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className={styles.uploadItemLink}
            onClick={(e) => e.stopPropagation()}
          >
            {item.type === UploadType.LINK
              ? item.url.replace(/^https?:\/\//i, "")
              : item.url.split("/").pop() || "File"}
          </a>
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeItem(idx);
            }}
            className={styles.uploadItemRemove}
          >
            <X size={10} />
          </button>
        </div>
      ))}
      <div className={styles.uploadActionsPopover}>{renderUploadMenu()}</div>
    </div>
  );

  const renderDefaultView = () => (
    <div className={styles.uploadItem}>
      {value[0].type === UploadType.LINK ? (
        <LinkIcon size={10} className={styles.uploadItemLinkIcon} />
      ) : (
        <FileText size={10} className={styles.uploadItemIcon} />
      )}
      <a
        href={value[0].url}
        target="_blank"
        rel="noreferrer"
        className={styles.uploadItemLink}
        onClick={(e) => e.stopPropagation()}
        title={value[0].url}
      >
        {(value[0].type === UploadType.LINK
          ? value[0].url.replace(/^https?:\/\//i, "")
          : value[0].url.split("/").pop() || "File"
        ).slice(0, 15)}
        {value.length > 1 ? "..." : ""}
      </a>
    </div>
  );

  return (
    <div
      className={styles.uploadCell}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {value.length === 0 ? (
        <div className={styles.uploadActions} ref={menuRef}>
          <Popover
            styles={{ body: { padding: 2 } }}
            content={renderUploadMenu()}
            trigger="click"
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            placement="bottomLeft"
          >
            <Button
              icon={<Plus size={12} />}
              size="small"
              className={styles.uploadAddButton}
              loading={uploading}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
            />
          </Popover>
        </div>
      ) : (
        <Popover
          styles={{ body: { padding: 8 } }}
          content={renderAllItems()}
          trigger="hover"
          placement="bottomLeft"
          mouseEnterDelay={0.3}
        >
          {renderDefaultView()}
        </Popover>
      )}

      <input
        type="file"
        ref={fileInputRef}
        multiple
        className={styles.hiddenFileInput}
        onChange={handleFileUpload}
        accept="*"
      />

      {onSave && onCancel && (
        <div className={styles.uploadCellActions}>
          <Button
            icon={<Check size={10} />}
            type="text"
            size="small"
            loading={isSaving}
            onClick={onSave}
            className={styles.editActionButton}
          />
          <Button
            icon={<Close size={10} />}
            type="text"
            size="small"
            onClick={onCancel}
            className={styles.editActionButton}
          />
        </div>
      )}
    </div>
  );
}

interface UploadCellViewProps {
  items: UploadItem[];
  onAddClick?: () => void;
}

export function UploadCellView({ items, onAddClick }: UploadCellViewProps) {
  return (
    <div className={styles.uploadItemsView}>
      {items.map((item, idx) => (
        <div key={idx} className={styles.uploadItemView}>
          {item.type === UploadType.LINK ? (
            <LinkIcon size={10} className={styles.uploadItemLinkIcon} />
          ) : (
            <FileText size={10} className={styles.uploadItemIcon} />
          )}
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className={styles.uploadItemLink}
            title={item.url}
            onClick={(e) => e.stopPropagation()}
          >
            {item.type === UploadType.LINK
              ? item.url.replace(/^https?:\/\//i, "")
              : item.url.split("/").pop() || "File"}
          </a>
        </div>
      ))}
      <div className={styles.uploadActionsView}>
        <Button
          icon={<Plus size={12} />}
          size="small"
          className={styles.uploadAddButton}
          onClick={(e) => {
            e.stopPropagation();
            onAddClick?.();
          }}
          type="text"
        />
      </div>
    </div>
  );
}
